import { getCustomLogo, getPartnerLogo } from "./partnerLogos";

import { Partner } from "Store/partners/partnerInterfaces";
import { getAccessToken } from "Utils/KeycloakWrapper";
import { isOfflineMode } from "Utils/isOfflineMode";
import { isAkademikerneTheme } from "Utils/partnerUtils";
import React from "react";
import { createRoot } from "react-dom/client";
import { isDefined } from "../functions";
import { getFooterPartnerProps } from "./footerPartnerProps";
import { getNavbarPartnerProps } from "./navbarPartnerProps";
import { ThemedPopover } from "./styled";

const navbarId = "navbar-root";
const footerId = "footer-root";

let navbarComponent: Promise<typeof import("navbar/init")>;
let footerComponent: Promise<typeof import("footer/init")>;
let footerComponents: Promise<typeof import("footer/components")>;

export function initialize(): void {
  if (isOfflineMode()) {
    return;
  }
  navbarComponent = import("navbar/init");
  footerComponent = import("footer/init");
  footerComponents = import("footer/components");
  const url = new URL(window.location.href);
  const themeKey = url.searchParams.get("grp");
  applyTheme(themeKey);
  renderNavbar(themeKey);
  renderFooter(themeKey);
}

export function updateNavbarCartNotification(themeKey: string | null): void {
  applyTheme(themeKey);
  renderNavbar(themeKey);
  renderFooter(themeKey);
}

const themes = {
  akademikerne: window.env.THEME_AKA,
  coop: window.env.THEME_COOP,
  danskebank: window.env.THEME_DANSKE_BANK,
  huseierne: window.env.THEME_HUSEIERNE,
} as Record<string, string>;

function applyTheme(themeKey: string | null): void {
  if (isDefined(themeKey) && themeKey !== Partner.STOREBRAND) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    if (isAkademikerneTheme(themeKey as Partner)) {
      link.href = themes.akademikerne;
    } else {
      link.href = themes[themeKey];
    }
    document.head.appendChild(link);
  }
}

export function renderNavbar(themeKey: string | null): void {
  if (isDefined(themeKey) && themeKey !== Partner.STOREBRAND) {
    initializePartnerNavbar(themeKey as Partner);
  } else {
    initializeNavbar();
  }
}

function renderFooter(themeKey: string | null): void {
  if (isDefined(themeKey) && themeKey !== Partner.STOREBRAND) {
    if (isAkademikerneTheme(themeKey as Partner)) {
      initializeAkaPartnerFooter();
    } else {
      initializePartnerFooter(themeKey as Partner);
    }
  } else {
    initializeFooter();
  }
}

function initializeNavbar(): void {
  if (navbarComponent) {
    navbarComponent.then((navbar) =>
      navbar.default(navbarId, {
        getToken: getAccessToken,
      }),
    );
  }
}

function initializePartnerNavbar(partnerId: Partner): void {
  if (navbarComponent) {
    navbarComponent.then((navbar) =>
      navbar.default(navbarId, {
        getToken: getAccessToken,
        show: {
          toggles: {
            notifications: false,
            menu: false,
            logout: true,
          },
        },
        ...getNavbarPartnerProps(partnerId),
      }),
    );
  }
}

function initializeFooter(): void {
  if (footerComponent) {
    footerComponent.then((footer) =>
      footer.default(footerId, {
        show: {
          finansportalen: true,
        },
      }),
    );
  }
}

function initializePartnerFooter(partnerId: Partner): void {
  if (footerComponents) {
    footerComponents.then((components) => {
      const footerContainer = document.getElementById(footerId);
      if (!isDefined(footerContainer)) {
        return;
      }

      const partnerProps = getFooterPartnerProps(partnerId);

      const {
        Footer,
        Top,
        Bottom,
        Grid,
        Column,
        DefaultLinks,
        Addon,
        Finansportalen,
        Copyright,
      } = components;

      const footerLeftComponents = (): React.ReactElement => {
        return (
          <Column>
            <DefaultLinks />
            <span className="overline" style={{ display: "inline-flex" }}>
              Gå til min profil og samtykker på storebrand.no
              <ThemedPopover partnerId={partnerId} showOn="hover">
                Når du åpner denne lenken kommer du til din kundeprofil på
                storebrand.no. En endring av samtykker og kontaktinformasjon
                gjelder for hele ditt kundeengasjement hos Storebrand.
              </ThemedPopover>
            </span>
            {partnerProps.show?.finansportalen && <Finansportalen />}
            <Copyright />
          </Column>
        );
      };

      createRoot(footerContainer).render(
        <Footer>
          <Top />
          <Bottom>
            {partnerProps.addons && (
              <Grid>
                {footerLeftComponents()}
                {partnerProps.addons && <Addon {...partnerProps.addons} />}
              </Grid>
            )}
            {!partnerProps.addons && footerLeftComponents()}
          </Bottom>
        </Footer>,
      );
    });
  }
}

function initializeAkaPartnerFooter(): void {
  if (footerComponents) {
    footerComponents.then((components) => {
      const footerContainer = document.getElementById(footerId);
      if (!isDefined(footerContainer)) {
        return;
      }

      const { Footer, Top, Bottom, Addon, Finansportalen } = components;
      createRoot(footerContainer).render(
        <Footer>
          <Top />
          <Bottom>
            <Addon
              logo={{
                src: getCustomLogo("akademikerne-plus-logo"),
                alt: "Akademikerne Plus",
                height: "28px",
                width: "auto",
                style: {
                  marginTop: "8px",
                },
              }}
            />
            <Addon
              text="I samarbeid med"
              logo={{
                src: getPartnerLogo(Partner.STOREBRAND),
                alt: "Storebrand",
                height: "16px",
                width: "auto",
                style: {
                  marginLeft: "4px",
                  marginTop: "0",
                },
              }}
              style={{
                alignItems: "center",
                flexDirection: "row",
                marginBottom: "16px",
                marginTop: "16px",
              }}
            />
            <Finansportalen />
          </Bottom>
        </Footer>,
      );
    });
  }
}
